import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import { CivilUnionNotificationsNonResidentCrumbs } from 'notifications-from-civil-status/constants/breadcrumb';
import { IdAmbito } from 'shell/constants/id-ambito';
import { CivilUnionNotificationsNonResidentSubscriptionCrumbs } from 'subscription/constants/breadcrumb';
import { CivilUnionNotificationsNonResidentMutationCrumbs } from 'mutation/constants/research-notifications-civil-status.crumbs';
import { CivilUnionNotificationsNonResidentCancellationsCrumbs } from 'cancellations/constants/breadcrumb';
import { getBaseUrl } from 'notifications-from-civil-status/utils/get-base-url';
import { useParams } from 'react-router-dom';
import { CivilUnionNotificationsNonResidentCivilStatusCrumbs } from 'civil-status/constants/breadcrumb';
import PersonSearchCivilUnionNonResidentNotificationShared from 'shell/components/shared/research-notifications/research-civil-union-non-resident/edit-person-search';
import { HttpResponse } from 'shell/models/http-response';
import { RegistrationCivilUnionFormVM } from 'mutation/view-models/registration-civil-union';
import { transformCivilUnion } from 'notifications-from-civil-status/utils/transform-civil-union';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ActCategory } from 'shell/models/act-category-list';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';

interface PersonSearchCivilUnionNonResidentNotificationCivilStatusSharedProps {
  scope?: string;
}

const PersonSearchCivilUnionNonResidentNotificationCivilStatusShared = ({
  scope,
}: PersonSearchCivilUnionNonResidentNotificationCivilStatusSharedProps): ReactElement => {
  const params = useParams();

  const idCat = params.idCat || undefined;
  const idAnsc = params.id || undefined;
  const idNotifica = params.idNotifica || '';

  const filterTable = useFilterTable<ResearchNotifications>();

  const goBackUrl: string = useMemo(() => {
    return `${getBaseUrl(scope, idCat)}/modifica/${idAnsc}/${idNotifica}`;
  }, [scope, idAnsc, idCat]);

  const redirectUrl: string = useMemo(() => {
    return `${goBackUrl}/unione-civile/non-residenti/registrazione/:idIntestatario/:idSchedaSoggettoANPR`;
  }, [goBackUrl]);

  const items = useMemo(() => {
    if (idCat) {
      return CivilUnionNotificationsNonResidentCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return CivilUnionNotificationsNonResidentCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return CivilUnionNotificationsNonResidentSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return CivilUnionNotificationsNonResidentMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return CivilUnionNotificationsNonResidentCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  }, [scope, idCat]);

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    RegistrationCivilUnionFormVM
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => {
      const castedRes = transformCivilUnion(res, filterTable.values?.isMaritoResidenceMilano);
      return castedRes.data;
    },
  });

  useEffect(() => {
    if (idAnsc) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  return (
    <Fragment>
      <PersonSearchCivilUnionNonResidentNotificationShared
        redirectUrl={redirectUrl}
        goBackUrl={`${goBackUrl}/unione-civile/non-residenti`}
        items={items}
        initialValues={dataEvent.data as RegistrationCivilUnionFormVM}
      />
    </Fragment>
  );
};

PersonSearchCivilUnionNonResidentNotificationCivilStatusShared.displayName =
  'Research CivilUnion Non Resident Notifications Page component';

export default PersonSearchCivilUnionNonResidentNotificationCivilStatusShared;
